import { RangeCalendar } from "@mantine/dates";
import { Popover } from "@mantine/core";
import { useState } from "react";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { ACCENT, getColors } from "./constants";
import { CalendarIcon, ClearIcon } from "./icons";
import { buildCalendarStyles } from "./calendarStyles";
import ToggleButton from "./ToggleButton";
import YearNavButton from "./YearNavButton";

const MonthAwareRangePicker = ({
  value,
  onChange,
  tooltip,
  placeholder,
  classNames,
  isDark,
  getDayStyle,
}) => {
  const [opened, setOpened] = useState(false);
  const [mode, setMode] = useState("day"); // "day" | "month"
  const [pendingStart, setPendingStart] = useState(null);
  const [hoveredMonth, setHoveredMonth] = useState(null); // Date | null
  const [displayYear, setDisplayYear] = useState(
    (value?.[0] || value?.[1] || new Date()).getFullYear()
  );

  const colors = getColors(isDark);

  // Clean inline style for the trigger input (no pseudo-selectors — those are
  // invalid as React inline styles).
  const inputStyle = {
    fontSize: "12px",
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: "6px",
    backgroundColor: colors.bg,
  };

  const dayCalendarStyles = buildCalendarStyles(colors);

  const startVal = value?.[0] || null;
  const endVal = value?.[1] || null;

  const displayText = (() => {
    if (!startVal || !endVal) return "";
    if (mode === "month") {
      // Show the snapped day range so the selection reads concretely, e.g.
      // "Jan 1, 2026 - Mar 31, 2026".
      return `${format(startVal, "MMM d, yyyy")} - ${format(endVal, "MMM d, yyyy")}`;
    }
    return `${format(startVal, "dd MMM yyyy")} – ${format(endVal, "dd MMM yyyy")}`;
  })();

  const handleDayChange = (val) => {
    onChange(val);
    if (val?.[0] && val?.[1]) {
      setOpened(false);
    }
  };

  const handleMonthClick = (monthIndex) => {
    const clicked = new Date(displayYear, monthIndex, 1);
    if (!pendingStart) {
      // First click: select the whole month as the range, wait for an end.
      setPendingStart(clicked);
      onChange([startOfMonth(clicked), endOfMonth(clicked)]);
    } else {
      let s = pendingStart;
      let e = clicked;
      if (e < s) {
        [s, e] = [e, s];
      }
      onChange([startOfMonth(s), endOfMonth(e)]);
      setPendingStart(null);
      setHoveredMonth(null);
      setOpened(false);
    }
  };

  // Bounds of the months to paint as a range. While a start is pending we
  // preview from it to whatever month is hovered (mirrors the day calendar's
  // hover-to-extend behaviour); otherwise we use the committed value.
  const monthRangeBounds = () => {
    if (pendingStart) {
      const a = startOfMonth(pendingStart);
      const b = startOfMonth(hoveredMonth || pendingStart);
      return a <= b ? { start: a, end: b } : { start: b, end: a };
    }
    if (startVal && endVal) {
      return { start: startOfMonth(startVal), end: startOfMonth(endVal) };
    }
    return null;
  };

  const getMonthState = (monthIndex) => {
    const bounds = monthRangeBounds();
    if (!bounds) return { inRange: false, edge: false };
    const t = startOfMonth(new Date(displayYear, monthIndex, 1)).getTime();
    return {
      inRange: t >= bounds.start.getTime() && t <= bounds.end.getTime(),
      edge: t === bounds.start.getTime() || t === bounds.end.getTime(),
    };
  };

  const switchMode = (next) => {
    if (next === mode) return;
    setMode(next);
    setPendingStart(null);
    setHoveredMonth(null);
  };

  const months = Array.from({ length: 12 }, (_, i) =>
    format(new Date(2000, i, 1), "MMM")
  );

  const handleClear = (e) => {
    e.stopPropagation();
    onChange([null, null]);
    setPendingStart(null);
    setHoveredMonth(null);
  };

  return (
    <div title={tooltip} onClick={(e) => e.stopPropagation()} className={classNames}>
      <Popover
        opened={opened}
        onChange={setOpened}
        position="bottom-start"
        withinPortal
        shadow="md"
        radius={6}
        zIndex={1100}
        styles={{
          dropdown: {
            backgroundColor: colors.bg,
            border: `1px solid ${colors.border}`,
            padding: 0,
          },
        }}
      >
        <Popover.Target>
          <div
            role="button"
            tabIndex={0}
            onClick={() => setOpened((o) => !o)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              ...inputStyle,
              padding: "8px 12px",
              minHeight: "36px",
            }}
          >
            <span style={{ display: "flex", flexShrink: 0 }}>
              <CalendarIcon isDark={isDark} />
            </span>
            <span
              style={{
                fontSize: "12px",
                color: displayText ? colors.text : isDark ? "#F6F7F7" : "#646B72",
                opacity: displayText ? 1 : 0.7,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                flex: 1,
              }}
            >
              {displayText || placeholder}
            </span>
            {displayText && (
              <button
                type="button"
                aria-label="Clear date range"
                onClick={handleClear}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  padding: 0,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: colors.subText,
                }}
              >
                <ClearIcon />
              </button>
            )}
          </div>
        </Popover.Target>

        <Popover.Dropdown>
          <div style={{ width: 300 }}>
            {/* Header: label + By day / By month toggle */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                padding: "12px",
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: colors.text,
                  whiteSpace: "nowrap",
                }}
              >
                Custom range
              </span>
              <div
                style={{
                  display: "flex",
                  gap: "2px",
                  padding: "2px",
                  borderRadius: "6px",
                  backgroundColor: colors.toggleBg,
                }}
              >
                <ToggleButton
                  label="By day"
                  active={mode === "day"}
                  onClick={() => switchMode("day")}
                  colors={colors}
                />
                <ToggleButton
                  label="By month"
                  active={mode === "month"}
                  onClick={() => switchMode("month")}
                  colors={colors}
                />
              </div>
            </div>

            {/* Body */}
            {mode === "day" ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "8px",
                }}
              >
                <RangeCalendar
                  value={[startVal, endVal]}
                  onChange={handleDayChange}
                  allowSingleDateInRange
                  dayStyle={getDayStyle}
                  styles={dayCalendarStyles}
                />
              </div>
            ) : (
              <div style={{ padding: "12px" }}>
                {/* Year navigation */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "12px",
                  }}
                >
                  <YearNavButton
                    label="‹"
                    colors={colors}
                    onClick={() => setDisplayYear((y) => y - 1)}
                  />
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: colors.text,
                    }}
                  >
                    {displayYear}
                  </span>
                  <YearNavButton
                    label="›"
                    colors={colors}
                    onClick={() => setDisplayYear((y) => y + 1)}
                  />
                </div>

                {/* Month grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "6px",
                  }}
                  onMouseLeave={() => setHoveredMonth(null)}
                >
                  {months.map((label, i) => {
                    const { inRange, edge } = getMonthState(i);
                    // Plain hover highlight only when not part of a range
                    // (range cells already get accent/in-range colours).
                    const isHover =
                      !inRange &&
                      hoveredMonth &&
                      startOfMonth(hoveredMonth).getTime() ===
                        startOfMonth(new Date(displayYear, i, 1)).getTime();
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() => handleMonthClick(i)}
                        onMouseEnter={() => setHoveredMonth(new Date(displayYear, i, 1))}
                        style={{
                          padding: "12px 0",
                          fontSize: "13px",
                          fontWeight: inRange ? 600 : 400,
                          cursor: "pointer",
                          border: "none",
                          borderRadius: "6px",
                          color: inRange && edge ? "#FFFFFF" : colors.text,
                          backgroundColor: inRange
                            ? edge
                              ? ACCENT
                              : colors.inRange
                            : isHover
                            ? colors.hover
                            : "transparent",
                          transition: "background-color 120ms ease",
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </Popover.Dropdown>
      </Popover>
    </div>
  );
};

export default MonthAwareRangePicker;
